import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject } from 'rxjs';

import { Languages } from '@shared';

import { AppConfig, APPCONFIG } from '../../configs/app-config';
import { CookieName } from '../../constants/cookie-name.enum';
import { SecurityType } from '../security/security-type.enum';
import { SessionItemName } from '../../constants/session-item-name.enum';

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    private readonly translateService = inject(TranslateService);
    private readonly cookieService = inject(CookieService);
    private readonly document = inject(DOCUMENT);
    private readonly appConfig = inject<AppConfig>(APPCONFIG);

    public userName: string;
    public requestId: string;
    public apiKey: string;
    public apiVersion: string;
    public optimizationRunning: boolean;
    public sessionExpired: boolean;
    public bearerToken: string;
    public municipalityName = '';

    private jobId: string;
    private readonly localhost: boolean;
    private readonly planName$: Subject<string>;

    constructor() {
        this.apiVersion = this.appConfig.apiVersion;
        this.localhost = this.document.location.hostname === 'localhost';
        this.planName$ = new Subject();
    }

    public init(): Promise<boolean> {
        return new Promise(resolve => {
            this.initDefaultAuthentication();
            resolve(true);
        });
    }

    public hasSession(): boolean {
        return !!this.requestId;
    }

    public setSession(requestId: string, username: string, apiKey: string, bearerToken?: string): void {
        const currentRequestId = this.cookieService.get(CookieName.RequestId);
        const currentUserName = this.cookieService.get(CookieName.UserName);
        const currentApiKey = this.cookieService.get(CookieName.ApiKey);
        const currentBearerToken = this.cookieService.get(CookieName.BearerToken);

        if (requestId !== currentRequestId ||
            username !== currentUserName ||
            apiKey !== currentApiKey ||
            (bearerToken && bearerToken !== currentBearerToken)) {
            this.cookieService.set(CookieName.RequestId, requestId, null, '/', null, !this.localhost);
            this.cookieService.set(CookieName.UserName, username, null, '/', null, !this.localhost );
            this.cookieService.set(CookieName.ApiKey, apiKey, null, '/', null, !this.localhost );
            this.cookieService.set(CookieName.BearerToken, bearerToken, null, '/', null, !this.localhost);

            window.location.reload();
        }
    }

    public getApiUrl(): string {
        if (!this.localhost) {
            return this.appConfig.apiUrl;
        }

        if (this.appConfig.localBackend) {
            return '/local';
        } else {
            const url = this.appConfig.production
                ? '/prod'
                : '/dev';
            return `${url}${SecurityType.Default}`;
        }
    }

    public SetLanguage(language?: string): Observable<any> {
        if (language) {
            this.cookieService.set(CookieName.Language, language, null, '/', null, !this.localhost, 'Strict');
            this.translateService.use(language);
        } else {
            language = this.cookieService.check(CookieName.Language)
                ? this.cookieService.get(CookieName.Language)
                : Languages.Norwegian;
        }

        return this.translateService.use(language);
    }

    public setPlanName(planName: string): void {
        this.planName$.next(planName);
    }

    public getPlanName(): Observable<string> {
        return this.planName$.asObservable();
    }

    public setJobId(jobId: string): void {
        this.jobId = jobId;
    }

    public getJobId(): string {
        return this.jobId;
    }

    public setMunicipalityName(municipalityName: string): void {
        this.municipalityName = municipalityName;
    }

    private initDefaultAuthentication(): void {
        this.requestId = this.cookieService.get(CookieName.RequestId);
        this.userName = this.cookieService.get(CookieName.UserName);
        this.apiKey = this.cookieService.get(CookieName.ApiKey);
        this.bearerToken = this.cookieService.get(CookieName.BearerToken);

        sessionStorage.setItem(SessionItemName.SecurityType, SecurityType.Default.toString());

        if (this.localhost && !this.requestId) {
            this.userName = this.appConfig.userName;
            this.requestId = this.appConfig.requestId;
            this.apiKey = this.appConfig.production ? this.appConfig.apiKeyProd : this.appConfig.apiKeyDev;
            this.bearerToken = this.appConfig.bearerToken;
        }
    }
}
